import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'pt' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    if (saved) return saved as Language;

    const browserLang = navigator.language.toLowerCase();
    const portugueseLangs = ['pt', 'pt-br', 'pt-pt'];

    return portugueseLangs.some(lang => browserLang.startsWith(lang)) ? 'pt' : 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key: string): string => {
    const keys = key.split('.');
    let value: any = translations[language];

    for (const k of keys) {
      value = value?.[k];
    }

    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const translations = {
  pt: {
    nav: {
      home: 'Início',
      services: 'Serviços',
      ai: 'Automação IA',
      web: 'Websites',
      social: 'Redes Sociais',
      seo: 'SEO & Ads',
      training: 'Formações',
      dpo: 'DPO Externo',
      work: 'Como Trabalhamos',
      contact: 'Falar Connosco',
      bookCall: 'Marcar chamada'
    },
    hero: {
      eyebrow: 'Estúdio Digital · Azambuja, Portugal',
      titleStart: 'Websites que vendem.',
      titleHighlight: 'Automação',
      titleEnd: 'que liberta o seu tempo.',
      subtitle: 'Damos vida online à sua pequena empresa com sites profissionais e automação com inteligência artificial — para vender enquanto dorme e parar de fazer tudo à mão.',
      cta: 'Marcar chamada gratuita',
      ctaSecondary: 'Ver serviços',
      stat1Value: '5 dias',
      stat1Label: 'até estar online',
      stat2Value: '24/7',
      stat2Label: 'atendimento automatizado',
      stat3Value: 'Desde €150',
      stat3Label: 'website profissional',
      stat4Value: '+10h/sem',
      stat4Label: 'poupadas em tarefas'
    },
    marquee: {
      items: [
        'Chatbots inteligentes',
        'Websites em 5 dias',
        'Automação com n8n',
        'Atendimento 24/7',
        'SEO local Portugal',
        'Workflows IA',
        'Google & Meta Ads',
        'Lead qualification'
      ]
    },
    services: {
      eyebrow: 'O que fazemos',
      title: 'Tudo o que precisa para crescer online.',
      subtitle: 'Seis serviços feitos à medida de pequenas empresas que querem aparecer, vender, poupar tempo e dormir descansadas.',
      seeAll: 'Saber mais',
      ai: {
        number: '01',
        tag: 'Featured',
        title: 'Automação com IA',
        oneLiner: 'Robôs que trabalham por si.',
        description: 'Chatbots, workflows com n8n, qualificação de leads, respostas automáticas e tarefas repetitivas — automatizados com inteligência artificial.',
        bullet1: 'Chatbots que respondem 24/7',
        bullet2: 'Automação de propostas e faturas',
        bullet3: 'Integração com email, CRM e WhatsApp'
      },
      web: {
        number: '02',
        title: 'Websites Profissionais',
        oneLiner: 'A sua nova montra digital.',
        description: 'Sites modernos, rápidos e otimizados para Google. Entrega em 5 dias, com domínio, hospedagem e SSL incluídos.',
        bullet1: 'Design responsivo mobile-first',
        bullet2: 'Otimização SEO de origem',
        bullet3: 'Formulários e analytics integrados'
      },
      social: {
        number: '03',
        title: 'Redes Sociais',
        oneLiner: 'Conteúdo que constrói marca.',
        description: 'Gestão completa de Facebook e Instagram com calendário editorial, criação visual e estratégia de crescimento.',
        bullet1: 'Calendário editorial mensal',
        bullet2: 'Criação visual e copywriting',
        bullet3: 'Relatórios mensais de performance'
      },
      seo: {
        number: '04',
        title: 'SEO & Ads',
        oneLiner: 'Apareça quando contam.',
        description: 'SEO local, Google Ads e Meta Ads otimizados para o seu orçamento. Foco em retorno mensurável, não em vaidade.',
        bullet1: 'SEO técnico e local',
        bullet2: 'Campanhas Google & Meta Ads',
        bullet3: 'Dashboards e otimização contínua'
      },
      training: {
        number: '05',
        title: 'Formações',
        oneLiner: 'Equipas que dominam IA e segurança.',
        description: 'Workshops práticos para a sua equipa: usar IA no dia-a-dia e proteger-se online. Sem jargão técnico, com exemplos reais.',
        bullet1: 'Formação IA aplicada ao trabalho',
        bullet2: 'Higiene Digital — cibersegurança para utilizadores',
        bullet3: 'Online ou presencial, em português claro'
      },
      dpo: {
        number: '06',
        title: 'DPO Externo',
        oneLiner: 'Conformidade RGPD sem dor de cabeça.',
        description: 'Encarregado de Proteção de Dados externo: comunicação à CNPD, registo de tratamentos, resposta a titulares e formação à equipa.',
        bullet1: 'DPO designado e ponto de contacto público',
        bullet2: 'Auditorias, DPIA e plano de remediação',
        bullet3: 'Apoio em violações de dados (72h)'
      }
    },
    useCases: {
      eyebrow: 'Casos de uso · IA',
      title: 'O que pode automatizar hoje.',
      subtitle: 'Casos reais que pequenas empresas portuguesas estão a usar para poupar tempo e ganhar clientes.',
      items: {
        leads: {
          title: 'Captação automática de leads',
          desc: 'Um chatbot no seu site qualifica o contacto, agenda a chamada no seu Calendly e envia tudo para o seu CRM enquanto dorme.'
        },
        whatsapp: {
          title: 'Atendimento via WhatsApp 24/7',
          desc: 'Responda às perguntas frequentes dos clientes, partilhe preços e marque visitas automaticamente, mesmo fora de horas.'
        },
        admin: {
          title: 'Faturação e propostas automáticas',
          desc: 'Quando recebe um pedido por email, a IA prepara a proposta, gera a fatura e envia-a — só tem de aprovar com um clique.'
        },
        content: {
          title: 'Conteúdo para redes sociais',
          desc: 'A partir de uma ideia, geramos posts para Facebook e Instagram, com imagens e copy adaptado à sua marca.'
        },
        reports: {
          title: 'Relatórios mensais automáticos',
          desc: 'Receba todas as 1ª de mês um resumo claro com vendas, tráfego, leads e onde focar — sem mexer numa folha de cálculo.'
        },
        reviews: {
          title: 'Pedido de reviews no momento certo',
          desc: 'A IA identifica clientes felizes e envia automaticamente um pedido para deixarem review no Google. Mais SEO local, mais confiança.'
        }
      }
    },
    process: {
      eyebrow: 'Como trabalhamos',
      title: 'Simples, transparente, rápido.',
      subtitle: 'Sem propostas de 20 páginas. Sem reuniões intermináveis. Quatro passos e está a vender.',
      steps: {
        step1: {
          num: '01',
          title: 'Chamada gratuita',
          desc: 'Conta-nos o que precisa em 30 minutos. Saímos com um plano e preço fixo.'
        },
        step2: {
          num: '02',
          title: 'Desenho à medida',
          desc: 'Desenhamos o site e/ou a automação. Aprovação por mensagem, sem PDFs intermináveis.'
        },
        step3: {
          num: '03',
          title: 'Entrega rápida',
          desc: 'Em 5–15 dias está online. Treinamos a sua equipa para usar tudo sem stress.'
        },
        step4: {
          num: '04',
          title: 'Continuamos consigo',
          desc: 'Manutenção, melhorias e novas automações sempre que precisar. Cancela quando quiser.'
        }
      }
    },
    faq: {
      eyebrow: 'Perguntas frequentes',
      title: 'Antes de marcar a chamada.',
      subtitle: 'As perguntas que ouvimos todas as semanas — respondidas em português claro.'
    },
    contact: {
      eyebrow: 'Vamos falar',
      title: 'Pronto para vender mais e trabalhar menos?',
      subtitle: 'Marque uma chamada gratuita de 30 minutos. Sem compromisso, sem pressão de venda. Sai com um plano concreto.',
      formTitle: 'Ou envie-nos uma mensagem',
      formSubtitle: 'Respondemos em menos de 24 horas, sempre.',
      name: 'Nome',
      email: 'Email',
      phone: 'Telefone',
      company: 'Empresa',
      message: 'Conte-nos sobre o seu projeto',
      send: 'Enviar mensagem',
      sending: 'A enviar...',
      success: 'Mensagem enviada com sucesso! Entraremos em contacto em breve.',
      error: 'Erro ao enviar mensagem. Por favor, tente novamente.',
      bookMeeting: 'Marque uma reunião',
      bookMeetingSubtitle: 'Agende um horário que lhe seja conveniente.',
      directContact: 'Ou contacte-nos diretamente:',
      page: {
        title: 'Contacto - Spawncore Marketing Digital',
        description: 'Entre em contacto com a Spawncore para discutir as suas necessidades de marketing digital e automação IA. Websites, SEO e gestão de redes sociais em Portugal.',
        breadcrumb: 'Contacto',
        orSendMessage: 'Ou envie-nos uma mensagem',
        formSubtitle: 'Preencha o formulário abaixo e responderemos em breve',
        namePlaceholder: 'O seu nome',
        emailPlaceholder: 'seu@email.com',
        phonePlaceholder: '+351 912 345 678',
        companyPlaceholder: 'Nome da sua empresa',
        messagePlaceholder: 'Como podemos ajudar?',
        optional: '(opcional)',
        required: '*',
        orContactDirect: 'Ou contacte-nos diretamente:',
        successTitle: 'Mensagem enviada!',
        successDesc: 'Obrigado pelo seu contacto. Responderemos em breve.',
        errorTitle: 'Erro ao enviar mensagem',
        errorDesc: 'Por favor, tente novamente ou contacte-nos diretamente por email.',
        validation: {
          nameMin: 'O nome deve ter pelo menos 2 caracteres',
          nameMax: 'O nome deve ter no máximo 100 caracteres',
          emailInvalid: 'Email inválido',
          emailMax: 'O email deve ter no máximo 255 caracteres',
          phoneMin: 'Telefone inválido',
          phoneMax: 'Telefone inválido',
          companyMax: 'O nome da empresa deve ter no máximo 100 caracteres',
          messageMin: 'A mensagem deve ter pelo menos 10 caracteres',
          messageMax: 'A mensagem deve ter no máximo 1000 caracteres'
        }
      }
    },
    footer: {
      tagline: 'Estúdio digital. Websites profissionais + automação IA. Para pequenas empresas em Portugal que querem crescer sem complicações.',
      based: 'Sediados em Azambuja · Trabalhamos com todo o país',
      company: 'Estúdio',
      about: 'Sobre Nós',
      services: 'Serviços',
      contact: 'Contacto',
      servicesMenu: {
        ai: 'Automação IA',
        web: 'Websites Profissionais',
        social: 'Redes Sociais',
        seo: 'SEO & Ads',
        training: 'Formações IA & Higiene Digital',
        dpo: 'DPO Externo (RGPD)'
      },
      legal: 'Legal',
      privacy: 'Política de Privacidade',
      terms: 'Termos de Serviço',
      rights: 'Todos os direitos reservados.'
    },
    cookie: {
      message: 'Utilizamos cookies para melhorar a sua experiência no nosso website.',
      accept: 'Aceitar',
      decline: 'Recusar'
    },
    notFound: {
      title: 'Página Não Encontrada',
      description: 'Desculpe, a página que procura não existe ou foi movida.',
      home: 'Voltar ao Início',
      back: 'Página Anterior'
    },
    servicesAi: {
      title: 'Automação com Inteligência Artificial',
      subtitle: 'Chatbots, workflows e agentes IA que tratam das tarefas repetitivas para si.',
      cta: 'Marcar chamada gratuita',
      whyUs: 'O Que Pode Automatizar',
      included: 'O Que Está Incluído',
      ready: 'Pronto para Libertar o Seu Tempo?',
      readyText: 'Marque uma chamada gratuita de 30 minutos e descubra exatamente o que dá para automatizar no seu negócio.',
      contact: 'Falar Connosco',
      features: {
        chatbots: { title: 'Chatbots Inteligentes', desc: 'Atendimento 24/7 no seu site ou WhatsApp, com qualificação de leads e agendamento automático.' },
        workflows: { title: 'Workflows com n8n', desc: 'Ligamos as suas ferramentas (email, CRM, calendário, faturação) num só fluxo automático.' },
        content: { title: 'Conteúdo & Relatórios', desc: 'IA que gera posts, descrições, relatórios mensais e propostas a partir de templates seus.' }
      },
      benefits: [
        'Análise dos seus processos atuais',
        'Desenho da automação à medida',
        'Implementação com n8n / Make / Zapier',
        'Integrações com WhatsApp, Gmail, Slack, CRM',
        'Chatbots com IA (OpenAI, Anthropic)',
        'Documentação e formação da equipa',
        'Manutenção e ajustes mensais',
        'Garantia de funcionamento'
      ]
    },
    servicesWeb: {
      title: 'Websites Profissionais',
      subtitle: 'Sites bonitos, rápidos e prontos a vender. Entrega em 5 dias.',
      cta: 'Pedir orçamento',
      whyUs: 'Porquê Escolher-nos?',
      included: 'O Que Está Incluído',
      ready: 'Pronto para Começar?',
      readyText: 'Entre em contacto connosco e vamos criar o website perfeito para o seu negócio',
      contact: 'Contactar Agora',
      features: {
        modern: { title: 'Design Moderno', desc: 'Websites com design profissional e atualizado que transmitem confiança' },
        responsive: { title: 'Totalmente Responsivo', desc: 'Perfeito em todos os dispositivos - desktop, tablet e mobile' },
        fast: { title: 'Rápido e Otimizado', desc: 'Performance otimizada para carregamento rápido e melhor experiência' }
      },
      benefits: ['Website personalizado para o seu negócio', 'Design responsivo e mobile-first', 'Otimização para motores de busca (SEO)', 'Integração com redes sociais', 'Formulários de contacto funcionais', 'Hospedagem e domínio incluídos', 'Certificado SSL para segurança', 'Suporte técnico contínuo']
    },
    servicesSocial: {
      title: 'Gestão de Redes Sociais',
      subtitle: 'Construa a presença da sua marca no Facebook, Instagram e muito mais',
      cta: 'Pedir orçamento',
      howWeHelp: 'Como Ajudamos o Seu Negócio',
      included: 'Serviços Incluídos',
      ready: 'Vamos Fazer Crescer a Sua Marca',
      readyText: 'Deixe-nos gerir as suas redes sociais enquanto foca no seu negócio',
      contact: 'Começar Agora',
      features: {
        content: { title: 'Gestão de Conteúdo', desc: 'Criação e publicação de conteúdo relevante e atrativo para o seu público' },
        growth: { title: 'Crescimento Orgânico', desc: 'Estratégias comprovadas para aumentar seguidores e engagement' },
        planning: { title: 'Planeamento Estratégico', desc: 'Calendário editorial personalizado para manter presença consistente' }
      },
      benefits: ['Gestão de Facebook e Instagram', 'Criação de conteúdo visual', 'Copywriting profissional', 'Agendamento de publicações', 'Interação com seguidores', 'Análise de métricas', 'Relatórios mensais', 'Estratégia de crescimento']
    },
    servicesSeo: {
      title: 'SEO & Ads',
      subtitle: 'Seja encontrado online com SEO e publicidade digital com retorno.',
      cta: 'Pedir orçamento',
      increase: 'Aumente a Sua Visibilidade Online',
      offer: 'O Que Oferecemos',
      ready: 'Pronto para Crescer?',
      readyText: 'Vamos criar uma estratégia de marketing digital à medida do seu negócio',
      contact: 'Falar Connosco',
      features: {
        seo: { title: 'Otimização SEO', desc: 'Melhore o ranking nos motores de busca e seja encontrado pelos seus clientes' },
        ads: { title: 'Publicidade Digital', desc: 'Campanhas direcionadas no Google Ads e Facebook Ads para máximo retorno' },
        analytics: { title: 'Análise e Relatórios', desc: 'Acompanhamento detalhado de métricas e resultados para otimização contínua' }
      },
      benefits: ['Análise de palavras-chave', 'Otimização on-page e técnica', 'Criação de conteúdo otimizado', 'Link building de qualidade', 'Configuração Google Analytics', 'Campanhas Google Ads', 'Campanhas Facebook/Instagram Ads', 'Relatórios de performance mensais']
    },
    privacy: {
      breadcrumb: 'Política de Privacidade',
      title: 'Política de Proteção de Dados',
      sections: {
        controller: {
          title: '1. Responsável pelo Tratamento',
          content: 'A Spawncore, com sede no Largo do Esteiro 6, 2050-261 Azambuja, Portugal, é a entidade responsável pelo tratamento dos seus dados pessoais.'
        }
      }
    }
  },
  en: {
    nav: {
      home: 'Home',
      services: 'Services',
      ai: 'AI Automation',
      web: 'Websites',
      social: 'Social Media',
      seo: 'SEO & Ads',
      training: 'Training',
      dpo: 'External DPO',
      work: 'How We Work',
      contact: 'Get in touch',
      bookCall: 'Book a call'
    },
    hero: {
      eyebrow: 'Digital Studio · Azambuja, Portugal',
      titleStart: 'Websites that sell.',
      titleHighlight: 'Automation',
      titleEnd: 'that gives you back your time.',
      subtitle: 'We bring small businesses online with professional websites and AI automation — so you sell while you sleep and stop doing everything by hand.',
      cta: 'Book a free call',
      ctaSecondary: 'See services',
      stat1Value: '5 days',
      stat1Label: 'to go live',
      stat2Value: '24/7',
      stat2Label: 'automated support',
      stat3Value: 'From €150',
      stat3Label: 'professional website',
      stat4Value: '+10h/week',
      stat4Label: 'saved on busywork'
    },
    marquee: {
      items: [
        'AI chatbots',
        'Websites in 5 days',
        'n8n automation',
        '24/7 customer service',
        'Local SEO Portugal',
        'AI workflows',
        'Google & Meta Ads',
        'Lead qualification'
      ]
    },
    services: {
      eyebrow: 'What we do',
      title: 'Everything you need to grow online.',
      subtitle: 'Six services built for small businesses that want to show up, sell, save time and sleep at night.',
      seeAll: 'Learn more',
      ai: {
        number: '01',
        tag: 'Featured',
        title: 'AI Automation',
        oneLiner: 'Robots that work for you.',
        description: 'Chatbots, n8n workflows, lead qualification, automatic replies and repetitive tasks — all automated with AI.',
        bullet1: 'Chatbots that answer 24/7',
        bullet2: 'Automated quotes and invoices',
        bullet3: 'Connects with email, CRM and WhatsApp'
      },
      web: {
        number: '02',
        title: 'Professional Websites',
        oneLiner: 'Your new digital storefront.',
        description: 'Modern, fast, Google-ready sites. Delivered in 5 days, with domain, hosting and SSL included.',
        bullet1: 'Mobile-first responsive design',
        bullet2: 'SEO baked-in from day one',
        bullet3: 'Forms and analytics included'
      },
      social: {
        number: '03',
        title: 'Social Media',
        oneLiner: 'Content that builds a brand.',
        description: 'Full Facebook and Instagram management — editorial calendar, visual creation and growth strategy.',
        bullet1: 'Monthly editorial calendar',
        bullet2: 'Visual creation and copywriting',
        bullet3: 'Monthly performance reports'
      },
      seo: {
        number: '04',
        title: 'SEO & Ads',
        oneLiner: 'Show up when it counts.',
        description: 'Local SEO, Google Ads and Meta Ads sized to your budget. Focused on measurable return, not vanity metrics.',
        bullet1: 'Technical and local SEO',
        bullet2: 'Google & Meta Ads campaigns',
        bullet3: 'Dashboards and continuous tuning'
      },
      training: {
        number: '05',
        title: 'Training',
        oneLiner: 'Teams that own AI & security.',
        description: 'Hands-on workshops for your team: using AI in daily work and staying safe online. No jargon, real examples.',
        bullet1: 'AI training applied to work',
        bullet2: 'Digital Hygiene — cybersecurity for users',
        bullet3: 'Online or in-person, plain language'
      },
      dpo: {
        number: '06',
        title: 'External DPO',
        oneLiner: 'GDPR compliance, painless.',
        description: 'External Data Protection Officer: CNPD filing, records of processing, data-subject responses and team training.',
        bullet1: 'Designated DPO and public point of contact',
        bullet2: 'Audits, DPIA and remediation plan',
        bullet3: 'Breach response support (72h)'
      }
    },
    useCases: {
      eyebrow: 'AI use cases',
      title: 'What you can automate today.',
      subtitle: 'Real workflows small businesses in Portugal are already using to save time and win clients.',
      items: {
        leads: {
          title: 'Automatic lead capture',
          desc: 'A chatbot on your site qualifies the contact, books the call on your Calendly and pushes everything to your CRM while you sleep.'
        },
        whatsapp: {
          title: '24/7 WhatsApp support',
          desc: 'Answer FAQs, share pricing and schedule visits automatically, even outside office hours.'
        },
        admin: {
          title: 'Automated quotes & invoices',
          desc: 'When a request lands in your inbox, AI drafts the proposal, generates the invoice and sends it — you just approve with one click.'
        },
        content: {
          title: 'Social media content',
          desc: 'From a single idea we generate Facebook and Instagram posts, with images and copy in your brand voice.'
        },
        reports: {
          title: 'Monthly auto-reports',
          desc: 'Every 1st of the month, get a clear summary — sales, traffic, leads and where to focus — without touching a spreadsheet.'
        },
        reviews: {
          title: 'Review requests at the right moment',
          desc: 'AI spots happy customers and triggers a Google review request automatically. More local SEO, more trust.'
        }
      }
    },
    process: {
      eyebrow: 'How we work',
      title: 'Simple, transparent, fast.',
      subtitle: 'No 20-page proposals. No endless meetings. Four steps and you are selling.',
      steps: {
        step1: {
          num: '01',
          title: 'Free 30-min call',
          desc: 'Tell us what you need. You leave with a plan and a fixed price.'
        },
        step2: {
          num: '02',
          title: 'Tailored design',
          desc: 'We design the site and/or automation. Approval by message — no endless PDFs.'
        },
        step3: {
          num: '03',
          title: 'Fast delivery',
          desc: 'In 5–15 days you are live. We train your team to use everything without stress.'
        },
        step4: {
          num: '04',
          title: 'We stay with you',
          desc: 'Maintenance, improvements and new automations whenever you need. Cancel anytime.'
        }
      }
    },
    faq: {
      eyebrow: 'FAQ',
      title: 'Before you book the call.',
      subtitle: 'The questions we hear every week — answered plainly.'
    },
    contact: {
      eyebrow: "Let's talk",
      title: 'Ready to sell more and work less?',
      subtitle: 'Book a free 30-minute call. No commitment, no sales pressure. Leave with a concrete plan.',
      formTitle: 'Or send us a message',
      formSubtitle: 'We reply within 24 hours, always.',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      company: 'Company',
      message: 'Tell us about your project',
      send: 'Send message',
      sending: 'Sending...',
      success: 'Message sent successfully! We will be in touch soon.',
      error: 'Error sending message. Please try again.',
      bookMeeting: 'Book a meeting',
      bookMeetingSubtitle: 'Pick a time that works for you.',
      directContact: 'Or contact us directly:',
      page: {
        title: 'Contact - Spawncore Digital Marketing',
        description: 'Get in touch with Spawncore to discuss your digital marketing and AI automation needs. Websites, SEO and social media management in Portugal.',
        breadcrumb: 'Contact',
        orSendMessage: 'Or send us a message',
        formSubtitle: 'Fill out the form below and we\'ll get back to you soon',
        namePlaceholder: 'Your name',
        emailPlaceholder: 'your@email.com',
        phonePlaceholder: '+351 912 345 678',
        companyPlaceholder: 'Your company name',
        messagePlaceholder: 'How can we help?',
        optional: '(optional)',
        required: '*',
        orContactDirect: 'Or contact us directly:',
        successTitle: 'Message sent!',
        successDesc: 'Thank you for contacting us. We\'ll respond shortly.',
        errorTitle: 'Error sending message',
        errorDesc: 'Please try again or contact us directly by email.',
        validation: {
          nameMin: 'Name must be at least 2 characters',
          nameMax: 'Name must be at most 100 characters',
          emailInvalid: 'Invalid email',
          emailMax: 'Email must be at most 255 characters',
          phoneMin: 'Invalid phone',
          phoneMax: 'Invalid phone',
          companyMax: 'Company name must be at most 100 characters',
          messageMin: 'Message must be at least 10 characters',
          messageMax: 'Message must be at most 1000 characters'
        }
      }
    },
    footer: {
      tagline: 'Digital studio. Professional websites + AI automation. For small businesses in Portugal that want to grow without complications.',
      based: 'Based in Azambuja · Working with the whole country',
      company: 'Studio',
      about: 'About Us',
      services: 'Services',
      contact: 'Contact',
      servicesMenu: {
        ai: 'AI Automation',
        web: 'Professional Websites',
        social: 'Social Media',
        seo: 'SEO & Ads',
        training: 'AI Training & Digital Hygiene',
        dpo: 'External DPO (GDPR)'
      },
      legal: 'Legal',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      rights: 'All rights reserved.'
    },
    cookie: {
      message: 'We use cookies to improve your experience on our website.',
      accept: 'Accept',
      decline: 'Decline'
    },
    notFound: {
      title: 'Page Not Found',
      description: 'Sorry, the page you are looking for does not exist or has been moved.',
      home: 'Back to Home',
      back: 'Previous Page'
    },
    servicesAi: {
      title: 'AI Automation',
      subtitle: 'Chatbots, workflows and AI agents that handle the repetitive work for you.',
      cta: 'Book a free call',
      whyUs: 'What You Can Automate',
      included: 'What\'s Included',
      ready: 'Ready to Get Your Time Back?',
      readyText: 'Book a free 30-minute call and find out exactly what we can automate in your business.',
      contact: 'Get in touch',
      features: {
        chatbots: { title: 'Smart Chatbots', desc: '24/7 customer support on your site or WhatsApp, with lead qualification and automatic scheduling.' },
        workflows: { title: 'n8n Workflows', desc: 'We connect your tools (email, CRM, calendar, invoicing) into a single automated flow.' },
        content: { title: 'Content & Reports', desc: 'AI that generates posts, descriptions, monthly reports and proposals from your own templates.' }
      },
      benefits: [
        'Audit of your current processes',
        'Tailored automation design',
        'Implementation with n8n / Make / Zapier',
        'Integrations with WhatsApp, Gmail, Slack, CRM',
        'AI chatbots (OpenAI, Anthropic)',
        'Documentation and team training',
        'Monthly maintenance and tuning',
        'Working-as-expected guarantee'
      ]
    },
    servicesWeb: {
      title: 'Professional Websites',
      subtitle: 'Beautiful, fast, ready-to-sell websites. Delivered in 5 days.',
      cta: 'Request a quote',
      whyUs: 'Why Choose Us?',
      included: 'What\'s Included',
      ready: 'Ready to Get Started?',
      readyText: 'Get in touch with us and we\'ll create the perfect website for your business',
      contact: 'Contact Now',
      features: {
        modern: { title: 'Modern Design', desc: 'Websites with professional and updated design that convey trust' },
        responsive: { title: 'Fully Responsive', desc: 'Perfect on all devices - desktop, tablet and mobile' },
        fast: { title: 'Fast and Optimized', desc: 'Optimized performance for fast loading and better experience' }
      },
      benefits: ['Custom website for your business', 'Responsive and mobile-first design', 'Search engine optimization (SEO)', 'Social media integration', 'Functional contact forms', 'Hosting and domain included', 'SSL certificate for security', 'Ongoing technical support']
    },
    servicesSocial: {
      title: 'Social Media Management',
      subtitle: 'Build your brand presence on Facebook, Instagram and more',
      cta: 'Request a quote',
      howWeHelp: 'How We Help Your Business',
      included: 'Services Included',
      ready: 'Let\'s Grow Your Brand',
      readyText: 'Let us manage your social media while you focus on your business',
      contact: 'Get Started',
      features: {
        content: { title: 'Content Management', desc: 'Creation and publication of relevant and engaging content for your audience' },
        growth: { title: 'Organic Growth', desc: 'Proven strategies to increase followers and engagement' },
        planning: { title: 'Strategic Planning', desc: 'Personalized editorial calendar to maintain consistent presence' }
      },
      benefits: ['Facebook and Instagram management', 'Visual content creation', 'Professional copywriting', 'Post scheduling', 'Follower interaction', 'Metrics analysis', 'Monthly reports', 'Growth strategy']
    },
    servicesSeo: {
      title: 'SEO & Ads',
      subtitle: 'Be found online with SEO and digital advertising that pays off.',
      cta: 'Request a quote',
      increase: 'Increase Your Online Visibility',
      offer: 'What We Offer',
      ready: 'Ready to Grow?',
      readyText: 'Let\'s create a digital marketing strategy tailored to your business',
      contact: 'Talk to Us',
      features: {
        seo: { title: 'SEO Optimization', desc: 'Improve your search engine ranking and be found by your customers' },
        ads: { title: 'Digital Advertising', desc: 'Targeted campaigns on Google Ads and Facebook Ads for maximum return' },
        analytics: { title: 'Analysis and Reports', desc: 'Detailed tracking of metrics and results for continuous optimization' }
      },
      benefits: ['Keyword analysis', 'On-page and technical optimization', 'Optimized content creation', 'Quality link building', 'Google Analytics setup', 'Google Ads campaigns', 'Facebook/Instagram Ads campaigns', 'Monthly performance reports']
    },
    privacy: {
      breadcrumb: 'Privacy Policy',
      title: 'Data Protection Policy',
      sections: {
        controller: {
          title: '1. Data Controller',
          content: 'Spawncore, headquartered at Largo do Esteiro 6, 2050-261 Azambuja, Portugal, is the entity responsible for processing your personal data.'
        }
      }
    }
  }
};
